import { Button, SelectV2, SelectV2Option, Spinner } from '@cimplico/components';
import { useEffect, useMemo, useState } from 'react';
import { toastNotification } from '../../../../components/toast/Toast';
import { Group } from '../../../../types/group';
import { useFYIGroups } from '../../../../queries/use-fyi-groups';
import { useCreateFYIGroupMapping } from '../../../../queries/use-create-fyi-group-map';

type FYIDocsGroupConfigurationProps = {
    group: Group;
    onReturn: () => void;
};

function FYIDocsGroupConfiguration({ group, onReturn }: FYIDocsGroupConfigurationProps): JSX.Element {
    const currentFyi = group.integrations.fyi;
    const fyiGroupQuery = useFYIGroups();
    const createFyiMappingQuery = useCreateFYIGroupMapping(group.id);

    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<SelectV2Option[]>([]);
    const [error, setError] = useState<string | undefined>(undefined);

    useEffect(() => {
        const groups = fyiGroupQuery.data || [];
        if (group.integrations.fyi) {
            // Find a matching entitty
            const matching = groups.find((g) => g.id === group.integrations.fyi?.id);
            setSelected(matching ? [{ label: matching.name, value: matching.id }] : []);
        } else {
            // Find a matching entitty
            const matching = groups.find((g) => g.name.toLowerCase() === group.name.toLowerCase());
            setSelected(matching ? [{ label: matching.name, value: matching.id }] : []);
        }
    }, [fyiGroupQuery.data, group]);

    function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();

        const selectedGroup = selected[0];

        if (!selectedGroup) {
            setError('Please select a group');
            return;
        }

        createFyiMappingQuery.mutate(
            {
                groupId: group.id,
                fyiGroupId: selectedGroup.value as number,
                fyiGroupName: selectedGroup.label,
            },
            {
                onSuccess: () => {
                    toastNotification('success', 'Successfully mapped group!');
                    onReturn();
                },
                onError: () => {
                    toastNotification('error', 'Unable to map group.');
                },
            }
        );
    }

    const options = useMemo(() => {
        if (!fyiGroupQuery.data) {
            return [];
        }
        return fyiGroupQuery.data
            .filter((e) => e.name.toLowerCase().includes(search.toLowerCase()))
            .map((e) => ({
                label: e.name,
                value: e.id,
                isDisabled: Boolean(e.existingMapping),
                disabledHint: 'This fyi group is already mapped.',
            }));
    }, [fyiGroupQuery, search]);

    if (fyiGroupQuery.isLoading) {
        return (
            <div className="flex items-center gap-2">
                <p className="text-sm text-gray-600">Getting clients from FYI...</p>
                <Spinner size="sm" />
            </div>
        );
    }
    if (fyiGroupQuery.isError || !fyiGroupQuery.isSuccess) {
        return <p>Error!</p>;
    }

    return (
        <div className="w-[400px]">
            <form onSubmit={handleSubmit}>
                <SelectV2
                    title="Available Groups"
                    options={options}
                    value={selected}
                    onSearch={setSearch}
                    onChange={(option): void => {
                        setSelected(option);
                        setError(undefined);
                    }}
                    errorMessage={error}
                    placeholder="Select a group..."
                />
                <div className="mt-4 float-right flex gap-4">
                    {currentFyi && <Button size="xs" type="hollow" label="Cancel" onClick={onReturn} />}
                    <Button label="Save" size="xs" type="primary" isSubmit />
                </div>
            </form>
        </div>
    );
}
export default FYIDocsGroupConfiguration;
